<template>
  <div class="anticipation-status-badge">
    <Tag
      v-if="status"
      :value="statusConfig.label"
      :severity="statusConfig.severity"
      :icon="statusConfig.icon"
      class="status-tag"
    />

    <!-- Anticipation details -->
    <div v-if="anticipation && showDetails" class="anticipation-details">
      <div v-if="anticipation.net_value_display" class="detail-item">
        <span class="detail-label">{{ t('Valor Liquido') }}:</span>
        <span class="detail-value">{{ formatAmount(anticipation.net_value) }}</span>
      </div>
      <div v-if="anticipation.fee" class="detail-item">
        <span class="detail-label">{{ t('Taxa') }}:</span>
        <span class="detail-value">{{ formatAmount(anticipation.fee) }} ({{ formatFeePercentage(anticipation.total_value, anticipation.fee) }})</span>
      </div>
      <div v-if="anticipation.anticipation_date" class="detail-item">
        <span class="detail-label">{{ t('Data Credito') }}:</span>
        <span class="detail-value">{{ formatDate(anticipation.anticipation_date) }}</span>
      </div>
      <div v-if="anticipation.due_date" class="detail-item">
        <span class="detail-label">{{ t('Vencimento Original') }}:</span>
        <span class="detail-value">{{ formatDate(anticipation.due_date) }}</span>
      </div>
      <div v-if="anticipation.anticipation_days" class="detail-item">
        <span class="detail-label">{{ t('Dias Antecipados') }}:</span>
        <span class="detail-value">{{ anticipation.anticipation_days }} {{ t('dias') }}</span>
      </div>
    </div>

    <!-- Action buttons -->
    <div v-if="showActions" class="action-buttons">
      <Button
        v-if="canCancelAnticipation"
        data-testid="anticipation-status-cancel-button"
        icon="pi pi-times"
        class="p-button-rounded p-button-text p-button-sm p-button-danger"
        v-tooltip.top="t('Cancelar antecipacao')"
        @click="handleCancel"
      />
      <Button
        v-if="canSync"
        data-testid="anticipation-status-sync-button"
        icon="pi pi-refresh"
        class="p-button-rounded p-button-text p-button-sm"
        v-tooltip.top="t('Sincronizar')"
        @click="handleSync"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { useAnticipations } from '../../composables/useAnticipations';
import { useDateFormatter } from '../../composables/useDateFormatter';

interface Anticipation {
  id?: number | string;
  status?: string;
  net_value?: number;
  net_value_display?: string;
  fee?: number;
  total_value?: number;
  anticipation_date?: string;
  due_date?: string;
  anticipation_days?: number;
  [key: string]: unknown;
}

interface StatusConfig {
  label: string;
  severity: string;
  icon: string;
}

const props = withDefaults(
  defineProps<{
    status?: string | null;
    anticipation?: Anticipation | null;
    showDetails?: boolean;
    showActions?: boolean;
  }>(),
  {
    status: null,
    anticipation: null,
    showDetails: false,
    showActions: false,
  }
);

const emit = defineEmits<{
  (e: 'cancel', anticipation: Anticipation): void;
  (e: 'sync', anticipation: Anticipation): void;
}>();

const { t } = useI18n();
const { getStatusConfig, formatAmount, formatFeePercentage, canCancel, isFinalStatus } = useAnticipations();
const { formatDate: formatDateLocale } = useDateFormatter();

// Computed
const currentStatus = computed<string | undefined | null>(() => {
  return props.status || props.anticipation?.status;
});

const statusConfig = computed<StatusConfig>(() => {
  return getStatusConfig(currentStatus.value) as StatusConfig;
});

const canCancelAnticipation = computed<boolean>(() => {
  return !!props.anticipation && canCancel(props.anticipation);
});

const canSync = computed<boolean>(() => {
  // Can sync if not a final status
  return !!props.anticipation && !isFinalStatus(props.anticipation.status);
});

// Methods
const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const handleCancel = (): void => {
  if (!props.anticipation?.id) return;

  log('AnticipationStatusBadge', 'handleCancel', 'Cancel requested', props.anticipation.id);
  emit('cancel', props.anticipation);
};

const handleSync = (): void => {
  if (!props.anticipation?.id) return;

  log('AnticipationStatusBadge', 'handleSync', 'Sync requested', props.anticipation.id);
  emit('sync', props.anticipation);
};
</script>

<style scoped>
.anticipation-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.status-tag {
  font-size: 0.75rem;
}

.anticipation-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: var(--surface-ground);
  border-radius: 4px;
  font-size: 0.75rem;
}

.detail-item {
  display: flex;
  gap: 0.5rem;
}

.detail-label {
  color: var(--text-color-secondary);
}

.detail-value {
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
}
</style>
